/**
 * PatternBar — Single pattern row: label, fill bar, score, trend indicator.
 *
 * Phase 1: Static bar width (no animation). Phase 3 will add Animated.Value spring.
 *
 * Bar colour by score:
 *   < 4.0: green (#34D399 → #10B981)
 *   < 6.5: amber (#FBBF24 → #F59E0B)
 *   ≥ 6.5: red   (#F87171 → #EF4444)
 *
 * Trend "stable" uses #6B7280 (not #A1A1AA — corrected for WCAG AA 4.7:1).
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { deriveSeverity } from '../practitioner-dashboard/ScoreChip';

type Trend = 'improving' | 'stable' | 'worsening';

interface PatternBarProps {
  name: string;
  score: number;
  trend: Trend;
  maxScore?: number;
  t: TFunction<'practitioner'>;
}

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 1, y: 0 } as const;

const TREND_CONFIG: Record<
  Trend,
  { icon: IoniconName; color: string; labelKey: string }
> = {
  improving: {
    icon: 'trending-down',
    color: '#059669',
    labelKey: 'trend.improving',
  },
  stable: {
    icon: 'remove',
    color: '#6B7280', // Corrected from #A1A1AA for WCAG AA
    labelKey: 'trend.stable',
  },
  worsening: {
    icon: 'trending-up',
    color: '#DC2626',
    labelKey: 'trend.worsening',
  },
};

const BAR_COLORS_BY_SEVERITY: Record<'low' | 'mid' | 'high', [string, string]> = {
  low: ['#34D399', '#10B981'],
  mid: ['#FBBF24', '#F59E0B'],
  high: ['#F87171', '#EF4444'],
};

function getBarColors(score: number): [string, string] {
  return BAR_COLORS_BY_SEVERITY[deriveSeverity(score)];
}

export const PatternBar: React.FC<PatternBarProps> = React.memo(function PatternBar({
  name,
  score,
  trend,
  maxScore = 10,
  t,
}) {
  const trendConfig = TREND_CONFIG[trend];
  const barColors = getBarColors(score);
  const fillPercent = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const trendLabel = t(trendConfig.labelKey as never);

  return (
    <View
      accessible={true}
      accessibilityLabel={t('pattern_bar.a11y' as never, { name, score, trend: trendLabel })}
    >
      {/* Top row: label + score + trend */}
      <View style={styles.topRow}>
        <Text style={styles.patternName}>{name}</Text>
        <View style={styles.scoreRow}>
          <Text style={styles.scoreValue}>{score.toFixed(1)}</Text>
          <Ionicons name={trendConfig.icon} size={12} color={trendConfig.color} />
          <Text style={[styles.trendLabel, { color: trendConfig.color }]}>
            {trendLabel}
          </Text>
        </View>
      </View>

      {/* Bar track */}
      <View style={styles.barTrack}>
        <LinearGradient
          colors={barColors}
          start={GRADIENT_START}
          end={GRADIENT_END}
          style={[styles.barFill, { width: `${fillPercent}%` as any }]}
        />
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  } as ViewStyle,
  patternName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#4B5563',
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  } as ViewStyle,
  scoreValue: {
    fontSize: 12,
    fontWeight: '700',
    color: '#1F2937',
  },
  trendLabel: {
    fontSize: 11,
    fontWeight: '500',
  },
  barTrack: {
    height: 8,
    borderRadius: 9999,
    backgroundColor: 'rgba(226, 232, 240, 0.8)',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 9999,
  },
});
